import {
  SearchComponent,
  CustomButton,
  CustomSelect,
  CustomSelectCheckbox,
} from '@/shared/ui/components';
import { DisableNextIcon, DisablePreviousIcon, nextIcon, previousIcon } from '@/shared/ui/assets';
import { STATUS_REPORT_OPTIONS } from '@/shared/config';
import { getttextPriority } from '@/shared/lib';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { renderPlaceHolderStatusReport } from '../../lib/inboxSearch';
import { useInboxSearchComponent } from './useInboxSearchComponent';
import SortInbox from './sortInbox';
import './style.scss';
import type { InboxSearchComponentProps } from './inboxSearchComponent.types';

const InboxSearchComponent = ({
  type,
  reportType,
  isLoadedData,
  inboxes,
  pageIndex,
  totalInboxCount,
  isEndOfData,
  navigatePage,
  onChangeSearchComponent,
  searchGoto,
  updateSearchGoto,
  removePrioritySelect = false,
  selectedTypeSeePatients,
  hiddenFilterStatus = false,
  handlePreventLoadingInboxDetails,
  selectedFacility,
}: InboxSearchComponentProps) => {
  const {
    state,
    priorityOptions,
    searchByList,
    onChange,
    onChangeSort,
    onClickPrevious,
    onClickNext,
    inboxesGotoRedux,
  } = useInboxSearchComponent({
    type,
    reportType,
    pageIndex,
    navigatePage,
    onChangeSearchComponent,
    searchGoto,
    updateSearchGoto,
    selectedTypeSeePatients,
    handlePreventLoadingInboxDetails,
    selectedFacility,
  });

  /**
   * Renders the 'showing x-y of n' pagination counter of the inbox search header from a fixed page size of ten. Clamps the upper bound to the total count, drops the 'Showing' prefix on small widths, and renders nothing when the total is zero.
   *
   * @param pageIndex - Zero-based index of the page being displayed.
   * @param maxDisplayedData - Total number of rows available for paging.
   * @returns The counter span, or false when there is nothing to page.
   */
  const pageNumberRenderer = (pageIndex: any, maxDisplayedData: any) => {
    const fromPage = pageIndex * 10 + 1;
    let toPage = pageIndex * 10 + 10;
    toPage = toPage >= maxDisplayedData ? maxDisplayedData : toPage;
    return (
      maxDisplayedData > 0 && (
        <span className="page-number-text">
          {`${!state.isSmallWidth ? 'Showing ' : ''}${fromPage}-${toPage} of ${maxDisplayedData} `}
        </span>
      )
    );
  };

  return (
    <div className="inbox-list__header-container">
      <div className="left-container">
        {isLoadedData ? (
          !!inboxes?.length && (
            <div className="navigation-group">
              <CustomButton
                id="navigation-left-btn"
                style="neutral"
                type="outline"
                size="small"
                className="navigation-left"
                icon={<ReactSVG src={pageIndex === 0 ? DisablePreviousIcon : previousIcon} />}
                disabled={pageIndex === 0}
                onClick={onClickPrevious}
                ariaLabel={t`Previous page`}
              />
              <CustomButton
                id="navigation-right-btn"
                style="neutral"
                type="outline"
                size="small"
                className="navigation-right"
                icon={
                  <ReactSVG
                    src={
                      (pageIndex ?? 0) * 10 + 10 >= (inboxes?.length ?? 0) && isEndOfData
                        ? DisableNextIcon
                        : nextIcon
                    }
                  />
                }
                disabled={(pageIndex ?? 0) * 10 + 10 >= (inboxes?.length ?? 0) && isEndOfData}
                onClick={onClickNext}
                ariaLabel={t`Next page`}
              />

              {pageNumberRenderer(pageIndex, totalInboxCount)}
            </div>
          )
        ) : (
          <div className="total-skeleton-container">
            <Skeleton width="50%" height="100%" />
          </div>
        )}
      </div>
      <div className="right-container">
        <SortInbox
          onChangeSort={onChangeSort}
          sortValue={state.sortValue}
          disabled={!isLoadedData}
          hasPriority={!removePrioritySelect}
        />
        {!removePrioritySelect && (
          <CustomSelect
            className="child-right"
            placeholder={`${t`Search`}...`}
            name="priority"
            id="priority-select"
            disabled={!isLoadedData}
            onChange={onChange}
            value={getttextPriority(state.priority)}
            options={_.map(priorityOptions.current, (item) => ({
              label: getttextPriority(item),
              value: item,
            }))}
            size="middle"
            showSearch
            allowClear={false}
            placement="bottomRight"
            aria-label={t`Priority`}
          />
        )}
        {!hiddenFilterStatus && (
          <CustomSelectCheckbox
            mode="multiple"
            className="child-right select-checkbox"
            placeholder={renderPlaceHolderStatusReport(state.statusReport)}
            ariaLabel={t`Status report`}
            name="statusReport"
            disabled={!isLoadedData}
            onChange={onChange}
            options={STATUS_REPORT_OPTIONS}
            size="middle"
            value={state.statusReport}
          />
        )}
        <SearchComponent
          searchValue={
            state.searchByValue === 'physicianId'
              ? inboxesGotoRedux.search.physicianSearchValue
              : state.searchValue
          }
          searchByValue={state.searchByValue}
          onChange={onChange}
          options={searchByList}
          optionsSelectValue={state.physicianOptions}
          disabled={!isLoadedData}
          inputType={state.searchByValue === 'studyFid' ? 'number' : 'text'}
          showSearch
        />
      </div>
    </div>
  );
};

export default InboxSearchComponent;
